import { supabase } from './supabase';
import { db } from './db';
import { newId } from './ids';
import { writable } from './schema';
import { toast } from '@/stores/toastStore';
import { unkeptFields, unkeptMessage } from './reconcile';

/**
 * Master data is edited online, from a desk, by a manager or administrator.
 * It is therefore written straight to Supabase rather than through the
 * offline outbox - the outbox exists for field capture, where the network
 * genuinely is not there.
 *
 * The local Dexie mirror is updated from the row the server returns, so the
 * UI reflects defaults and generated columns the database filled in rather
 * than what the form guessed.
 */

export class CrudError extends Error {
  constructor(message: string, readonly code?: string) {
    super(message);
  }
}

/** Turn a Postgres error into something a supervisor can act on. */
function explain(error: { message: string; code?: string; details?: string }): string {
  const code = error.code ?? '';
  const msg = error.message ?? 'Unknown error';

  if (code === '23505') return 'That code or number is already in use.';
  if (code === '23503') return 'Another record depends on this one, or a linked record is missing.';
  if (code === '23514' || code === 'P0001') {
    // Our own check constraints and triggers carry readable messages.
    return msg.replace(/^new row for relation "[^"]+" violates /, '');
  }
  if (code === '42501' || msg.includes('row-level security')) {
    return 'Your role does not allow this change.';
  }
  if (msg.includes('Failed to fetch') || msg.includes('NetworkError')) {
    return 'No connection to the server. Master data changes need to be online.';
  }
  return msg;
}

export async function upsert<T extends Record<string, unknown>>(
  table: string,
  values: T & { id?: string },
): Promise<T & { id: string }> {
  // A drawer edits a row it loaded from the mirror and sends the whole
  // thing back, generated columns included. Postgres refuses those outright,
  // so they come off here rather than in each caller.
  const payload = writable(table, { ...values, id: values.id ?? newId() });

  const { data, error } = await supabase
    .from(table)
    .upsert(payload as never)
    .select()
    .single();

  if (error) throw new CrudError(explain(error), error.code);

  await db.table(table).put(data);

  // Accepted is not the same as kept: a trigger may hold a column at its
  // old value. Say so, rather than let "saved" stand over it (0046).
  const lost = unkeptFields(table, payload, data as Record<string, unknown>);
  if (lost.length) toast.error(unkeptMessage(lost));

  return data as T & { id: string };
}

/** Soft delete - the row stays for the audit trail and replicates the
 *  deletion to offline clients through pull_changes(). */
export async function softDelete(table: string, id: string): Promise<void> {
  const { error } = await supabase
    .from(table)
    .update({ deleted_at: new Date().toISOString() })
    .eq('id', id);

  if (error) throw new CrudError(explain(error), error.code);
  await db.table(table).delete(id);
}

export async function restore(table: string, id: string): Promise<void> {
  const { error } = await supabase.from(table).update({ deleted_at: null }).eq('id', id);
  if (error) throw new CrudError(explain(error), error.code);
}

/** Wraps a save so every screen reports success and failure the same way. */
export async function save<T>(
  action: () => Promise<T>,
  successMessage: string,
): Promise<T | null> {
  try {
    const result = await action();
    toast.success(successMessage);
    return result;
  } catch (err) {
    toast.error(err instanceof CrudError ? err.message : String(err));
    return null;
  }
}
